import { useId, type ReactNode } from 'react';
import { motion } from 'motion/react';
import { cn } from './util';

export interface ToggleProps {
  checked: boolean;
  onChange: (checked: boolean) => void;
  label: ReactNode;
  /** Short state captions shown inside the track (default AÇIK / KAPALI). */
  onText?: string;
  offText?: string;
  disabled?: boolean;
  /** Hide the visible label (still used as the accessible name). */
  hideLabel?: boolean;
  className?: string;
}

/** Chunky on/off switch (`role="switch"`), Space/Enter toggle. */
export function Toggle({
  checked,
  onChange,
  label,
  onText = 'AÇIK',
  offText = 'KAPALI',
  disabled,
  hideLabel,
  className,
}: ToggleProps) {
  const id = useId();
  return (
    <span className={cn('inline-flex items-center gap-2.5', className)}>
      <button
        type="button"
        role="switch"
        aria-checked={checked}
        aria-labelledby={`${id}-l`}
        aria-disabled={disabled || undefined}
        onClick={() => !disabled && onChange(!checked)}
        className={cn(
          // Phones: a finger-sized (40px) track; the compact one starts at `sm`.
          'comic relative inline-flex h-10 w-[84px] shrink-0 items-center border-3 border-ink text-[13px] shadow-ink-xs transition-colors duration-150 sm:h-8 sm:w-[74px]',
          checked ? 'bg-jury text-white' : 'bg-white text-ink',
          disabled && 'opacity-55 grayscale',
        )}
      >
        <span className={cn('absolute pt-[2px]', checked ? 'left-2' : 'right-1.5')}>
          {checked ? onText : offText}
        </span>
        <motion.span
          aria-hidden
          layout
          transition={{ type: 'spring', stiffness: 700, damping: 30 }}
          className={cn(
            'absolute top-[2px] size-[30px] border-3 border-ink bg-yellow sm:size-[22px]',
            checked ? 'right-[2px]' : 'left-[2px]',
          )}
        />
      </button>
      <span id={`${id}-l`} className={cn('font-extrabold', hideLabel && 'sr-only')}>
        {label}
      </span>
    </span>
  );
}
